import { useState } from 'react';
import { Layout, Menu, Button, Space, Drawer } from 'antd';
import {
  MenuOutlined,
  HomeOutlined,
  ShoppingOutlined,
  UserOutlined,
  ShopOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { LocationSelector } from './LocationSelector';
import logoUrl from '../../../../packages/shared/assets/logo/logo_rescue_foodies_b.png';

const { Header } = Layout;

export const AppHeader = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/">Home</Link>,
    },
    {
      key: 'browse',
      icon: <SearchOutlined />,
      label: <Link to="/marketplace">Browse</Link>,
    },
    ...(isAuthenticated
      ? [
        {
          key: 'orders',
          icon: <ShoppingOutlined />,
          label: <Link to="/orders">My Orders</Link>,
        },
        {
          key: 'profile',
          icon: <UserOutlined />,
          label: <Link to="/profile">Profile</Link>,
        },
      ]
      : []),
    {
      key: 'for-businesses',
      icon: <ShopOutlined />,
      label: <Link to="/for-businesses">For Businesses</Link>,
    },
  ];

  return (
    <Header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        minHeight: '64px',
        height: 'auto',
      }}
    >
      {/* Logo - Optimized for Mobile */}
      <Link
        to="/"
        style={{
          display: 'flex',
          alignItems: 'center',
          flex: '0 1 auto',
          minWidth: 0,
        }}
      >
        <img
          src={logoUrl}
          alt="Rescue Foodies"
          style={{
            height: '48px',
            width: 'auto',
            maxWidth: '180px',
          }}
          className="app-logo"
        />
      </Link>

      {/* Desktop Menu */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          flex: 1,
          justifyContent: 'center',
        }}
        className="desktop-menu"
      >
        <Menu
          mode="horizontal"
          items={menuItems}
          style={{ border: 'none', flex: 1, justifyContent: 'center' }}
        />
      </div>

      {/* Location Selector & Auth Buttons - Responsive */}
      <Space size="small" style={{ flexShrink: 0 }}>
        {/* Location Selector - Show on desktop, hide on mobile */}
        <div className="desktop-location">
          <LocationSelector />
        </div>

        {/* Auth Section */}
        {isAuthenticated ? (
          <>
            {/* Desktop: Show full greeting */}
            <span className="desktop-greeting" style={{ fontSize: '14px' }}>
              Hi, {user?.name || 'User'}!
            </span>
            {/* Mobile: Show avatar only */}
            <div
              className="mobile-avatar"
              style={{
                display: 'none',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#4A7C59',
                color: 'white',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </div>
            <Button
              onClick={handleLogout}
              className="desktop-logout"
              size="middle"
            >
              Logout
            </Button>
          </>
        ) : (
          <>
            <Link to="/login">
              <Button size="middle" className="desktop-auth">
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button type="primary" size="middle">
                Register
              </Button>
            </Link>
          </>
        )}

        {/* Mobile Menu Icon */}
        <Button
          className="mobile-menu-button"
          data-testid="mobile-menu-button"
          type="text"
          aria-label="Menu"
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
          style={{
            display: 'none',
            minWidth: '44px',
            minHeight: '44px',
          }}
        />
      </Space>

      {/* Mobile Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{
          body: { paddingTop: '16px' },
        }}
      >
        {/* Location Selector in Mobile Drawer */}
        <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>
            Location
          </div>
          <LocationSelector />
        </div>

        {/* Navigation Menu */}
        <Menu
          mode="vertical"
          items={menuItems}
          onClick={() => setDrawerVisible(false)}
        />

        {/* Logout Button in Drawer (if authenticated) */}
        {isAuthenticated && (
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
            <Button
              onClick={() => {
                handleLogout();
                setDrawerVisible(false);
              }}
              block
              size="large"
              danger
            >
              Logout
            </Button>
          </div>
        )}
      </Drawer>

      <style>{`
        /* Logo responsive sizing */
        .app-logo {
          height: 48px;
          max-width: 180px;
        }
        
        @media (max-width: 768px) {
          .app-logo {
            height: 40px;
            max-width: 120px;
          }
        }
        
        /* Mobile optimizations (<768px) */
        @media (max-width: 768px) {
          .desktop-menu {
            display: none !important;
          }
          .mobile-menu-button {
            display: inline-flex !important;
          }
          
          /* Hide location selector on mobile */
          .desktop-location {
            display: none !important;
          }
          
          /* Hide full greeting on mobile */
          .desktop-greeting {
            display: none !important;
            
          }
          
          /* Hide logout button text on mobile */
          .desktop-logout {
            display: none !important;
          }
          
          /* Show avatar on mobile */
          .mobile-avatar {
            display: flex !important;
          }
        }
        
        /* Desktop styles */
        @media (min-width: 769px) {
          .mobile-menu-button {
            display: none !important;
          }
          .mobile-avatar {
            display: none !important;
          }
        }
      `}</style>
    </Header>
  );
};
